import 'dayjs/locale/en-ca'
import 'dayjs/locale/fr'
import 'dayjs/locale/fr-ca'

import { useMemo, useRef } from 'react'

import { useQueries, useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { sortBy, uniq } from 'lodash-es'

import { operatingRegionsQuery, regionsQuery, statsTransportRequestsQuery } from '@/state/regions/regions'
import { filterActiveRegions } from '@/state/regions/regionUtils'

import { buildChartData, buildClosedEveningData, CHART_COLORS, useChartNow } from './chartUtils'

// Enable ISO week plugin for dayjs
dayjs.extend(isoWeek)

/**
 * Normalize locale string for dayjs compatibility.
 * Converts 'fr-CA' to 'fr-ca', handles 'fr' -> 'fr-ca' for Canadian French preference.
 */
const normalizeDayjsLocale = (locale: string): string => {
  const lower = locale.toLowerCase()
  // Map base 'fr' to 'fr-ca' since this is a Quebec-based app
  if (lower === 'fr') return 'fr-ca'
  if (lower === 'en') return 'en-ca'
  return lower
}

// ============================================================================
// ISO Week Utilities (Soirées Équivalentes)
// ============================================================================

/**
 * Get the ISO week number for a given date (1-52/53).
 * ISO weeks start on Monday and week 1 is the week containing January 4th.
 */
export const getIsoWeek = (date: Date): number => dayjs(date).isoWeek()

/**
 * Get the ISO weekday for a given date (1=Monday, 7=Sunday).
 */
export const getIsoWeekday = (date: Date): number => dayjs(date).isoWeekday()

/**
 * Get the campaign year for a given date.
 * November-December dates belong to their calendar year's campaign.
 * January dates (if campaign extends) belong to the previous year's campaign.
 */
export const getCampaignYear = (date: Date): number => {
  const d = dayjs(date)
  const month = d.month() // 0-indexed: 0 = January, 10 = November
  // November (10) and December (11) belong to current year's campaign
  // January (0) would belong to previous year's campaign (if campaign extends)
  return month >= 10 ? d.year() : d.year() - 1
}

// ============================================================================
// Reusable Date Filtering Utilities
// ============================================================================

export interface ClosedDateInfo {
  date: string // YYYY-MM-DD
  label: string
}

/**
 * Find all dates with the same weekday in the specified campaign year.
 * Used by both the full stats page and mini stats.
 */
export const findDatesByWeekday = (
  closedDates: ClosedDateInfo[],
  weekday: number, // 0-6 (Sunday = 0)
  campaignYear: number
): ClosedDateInfo[] =>
  closedDates.filter((d) => {
    const date = dayjs(d.date)
    return getCampaignYear(date.toDate()) === campaignYear && date.day() === weekday
  })

/**
 * Find all dates with the same ISO week and weekday.
 * Used by both the full stats page and mini stats.
 */
export const findDatesByIsoWeek = (
  closedDates: ClosedDateInfo[],
  targetIsoWeek: number,
  targetIsoWeekday: number
): Array<ClosedDateInfo & { year: number }> =>
  closedDates
    .filter((d) => {
      const dateObj = dayjs(d.date).toDate()
      return getIsoWeek(dateObj) === targetIsoWeek && getIsoWeekday(dateObj) === targetIsoWeekday
    })
    .map((d) => ({
      ...d,
      year: dayjs(d.date).year(),
    }))
    .sort((a, b) => b.year - a.year) // Most recent first

// ============================================================================
// Selection Mode Types
// ============================================================================

export type SelectionMode = 'manual' | 'fixedDate' | 'weekday' | 'isoWeek'

export const FIXED_DATE_OPTIONS = [
  { day: 23, month: 12, labelKey: 'stats.fixedDate.dec23' },
  { day: 24, month: 12, labelKey: 'stats.fixedDate.dec24' },
  { day: 31, month: 12, labelKey: 'stats.fixedDate.dec31' },
] as const

export const WEEKDAY_OPTIONS = [
  { value: 0, labelKey: 'stats.weekday.sunday' },
  { value: 1, labelKey: 'stats.weekday.monday' },
  { value: 2, labelKey: 'stats.weekday.tuesday' },
  { value: 3, labelKey: 'stats.weekday.wednesday' },
  { value: 4, labelKey: 'stats.weekday.thursday' },
  { value: 5, labelKey: 'stats.weekday.friday' },
  { value: 6, labelKey: 'stats.weekday.saturday' },
] as const

// Special value for "now" (current operating evening)
export const NOW_DATE = 'now'

export interface AvailableRegion {
  regionId: string
  regionName: string
}

export interface AvailableDate {
  date: string // YYYY-MM-DD or 'now'
  label: string // "samedi 7 décembre 2024" or "Maintenant"
}

export interface SelectedDate {
  date: string // YYYY-MM-DD or 'now'
  label: string
  color: string
  eveningIds: number[] // Evening IDs to query (filtered by region if specific region selected)
}

export interface ChartDataPoint {
  time: string // "17:00", "17:15", etc.
  timeMinutes: number // Minutes from 5PM (0 = 17:00, 15 = 17:15, etc.)
  [dateKey: string]: number | string | null // cumulative count per date (null for future times on today)
}

export interface UseStatsPageOptions {
  regionId: string | undefined // undefined means "all regions"
  mode: SelectionMode
  // For mode 'manual'
  dates: string[] // YYYY-MM-DD format
  // For mode 'fixedDate'
  fixedDay: number | undefined // 23, 24, or 31
  // For mode 'weekday'
  weekday: number | undefined // 0-6 (Sunday = 0)
  // For mode 'isoWeek'
  referenceDate: string | undefined // YYYY-MM-DD or 'now'
  // Locale for date formatting (from intl.locale)
  locale: string // e.g., "fr-ca", "en-ca"
  // Translated strings (to avoid hardcoded French in the hook)
  nowLabel: string // e.g., "Maintenant" or "Now"
  nowSuffix: string // e.g., "(maintenant)" or "(now)"
}

export interface CurrentCampaignDate {
  date: string // YYYY-MM-DD or 'now'
  label: string
  isoWeek: number // 1-52/53
  isoWeekday: number // 1=Monday, 7=Sunday
}

export interface UseStatsPageReturn {
  availableRegions: AvailableRegion[]
  availableDates: AvailableDate[] // For manual mode
  currentCampaignDates: CurrentCampaignDate[] // For isoWeek mode (current year dates)
  selectedDates: SelectedDate[]
  chartData: ChartDataPoint[]
  isLoading: boolean // True only on initial load (no cached data)
  isFetching: boolean // True when fetching, even if we have stale/cached data
}

export const useStatsPage = ({
  regionId,
  mode,
  dates,
  fixedDay,
  weekday,
  referenceDate,
  locale: rawLocale,
  nowLabel,
  nowSuffix,
}: UseStatsPageOptions): UseStatsPageReturn => {
  // Normalize locale for dayjs compatibility (e.g., 'fr-CA' -> 'fr-ca')
  const locale = normalizeDayjsLocale(rawLocale)

  // Current time for chart calculations, optimized to only update when timeMinutes changes
  const now = useChartNow()

  // Fetch all regions and operating regions (same as useOperatingRegionsPage)
  const { data: regionsSnapshot } = useSuspenseQuery(regionsQuery())
  const { data: operatingRegionsSnapshot } = useSuspenseQuery(operatingRegionsQuery())

  // Build available regions list (non-test regions)
  const availableRegions = useMemo(
    (): AvailableRegion[] =>
      sortBy(
        regionsSnapshot.data
          .filter((region) => !region.test)
          .map((region) => ({
            regionId: region._id,
            regionName: region.name ?? 'Unknown',
          })),
        'regionName'
      ),
    [regionsSnapshot.data]
  )

  // Check if there are currently operating regions (for "now" option)
  const { hasOperatingRegions, nowEveningIdMap } = useMemo(() => {
    const activeRegions = filterActiveRegions(operatingRegionsSnapshot.data)
    const map: Record<string, number> = {}
    activeRegions.forEach((region) => {
      if (region.currentOperatingDate?.eveningId) {
        map[region._id] = region.currentOperatingDate.eveningId
      }
    })
    return {
      hasOperatingRegions: Object.keys(map).length > 0,
      nowEveningIdMap: map,
    }
  }, [operatingRegionsSnapshot.data])

  // Build closed evening data (shared utility handles the common logic)
  const { closedDates: availableClosedDates, eveningsByDateAndRegion: closedEveningsByDateAndRegion } = useMemo(
    () => buildClosedEveningData(regionsSnapshot.data, locale),
    [regionsSnapshot.data, locale]
  )

  // Combine "now" option with closed dates for the full available dates list (for manual mode)
  const availableDates = useMemo((): AvailableDate[] => {
    const result: AvailableDate[] = []
    if (hasOperatingRegions) {
      result.push({ date: NOW_DATE, label: nowLabel })
    }
    result.push(...availableClosedDates)
    return result
  }, [hasOperatingRegions, availableClosedDates, nowLabel])

  // Build list of current campaign dates (for isoWeek mode)
  const currentCampaignYear = getCampaignYear(new Date())
  const currentCampaignDates = useMemo((): CurrentCampaignDate[] => {
    const result: CurrentCampaignDate[] = []

    // Add "now" if there's an operating region
    if (hasOperatingRegions) {
      const nowDate = new Date()
      result.push({
        date: NOW_DATE,
        label: nowLabel,
        isoWeek: getIsoWeek(nowDate),
        isoWeekday: getIsoWeekday(nowDate),
      })
    }

    // Add closed dates from current campaign year
    availableClosedDates
      .filter((d) => getCampaignYear(dayjs(d.date).toDate()) === currentCampaignYear)
      .forEach((d) => {
        const dateObj = dayjs(d.date).toDate()
        result.push({
          date: d.date,
          label: d.label,
          isoWeek: getIsoWeek(dateObj),
          isoWeekday: getIsoWeekday(dateObj),
        })
      })

    return result
  }, [hasOperatingRegions, availableClosedDates, currentCampaignYear, nowLabel])

  // Helper to get evening IDs for a specific date
  const getEveningIdsForDate = (date: string): number[] => {
    if (date === NOW_DATE) {
      if (regionId) {
        const eveningId = nowEveningIdMap[regionId]
        return eveningId ? [eveningId] : []
      }
      return Object.values(nowEveningIdMap)
    }
    if (regionId) {
      const eveningId = closedEveningsByDateAndRegion[date]?.[regionId]
      return eveningId ? [eveningId] : []
    }
    return Object.values(closedEveningsByDateAndRegion[date] ?? {})
  }

  // Determine which dates to display based on selection mode
  const selectedDatesWithEveningIds = useMemo((): SelectedDate[] => {
    switch (mode) {
      case 'manual':
        // Original behavior: user-selected dates
        return dates.map((date, index) => {
          const availableDate = availableDates.find((d) => d.date === date)
          return {
            date,
            label: availableDate?.label ?? date,
            color: CHART_COLORS[index % CHART_COLORS.length],
            eveningIds: getEveningIdsForDate(date),
          }
        })

      case 'fixedDate': {
        // Find all years that have this fixed date (23, 24, or 31 December)
        if (!fixedDay) return []
        const matchingDates = availableClosedDates.filter((d) => {
          const date = dayjs(d.date)
          return date.month() === 11 && date.date() === fixedDay // December = month 11
        })
        // Also check if "now" is this date
        const nowDate = dayjs()
        const isNowThisDate = hasOperatingRegions && nowDate.month() === 11 && nowDate.date() === fixedDay

        const result: SelectedDate[] = []
        if (isNowThisDate) {
          result.push({
            date: NOW_DATE,
            label: `${nowDate.locale(locale).format('D MMMM YYYY')} ${nowSuffix}`,
            color: CHART_COLORS[0],
            eveningIds: getEveningIdsForDate(NOW_DATE),
          })
        }
        matchingDates.forEach((d) => {
          result.push({
            date: d.date,
            label: dayjs(d.date).locale(locale).format('D MMMM YYYY'),
            color: CHART_COLORS[result.length % CHART_COLORS.length],
            eveningIds: getEveningIdsForDate(d.date),
          })
        })
        return result
      }

      case 'weekday': {
        // Find all dates of this weekday in the current campaign
        if (weekday === undefined) return []
        const matchingDates = availableClosedDates.filter((d) => {
          const date = dayjs(d.date)
          return getCampaignYear(date.toDate()) === currentCampaignYear && date.day() === weekday
        })
        // Also check if "now" is this weekday and current campaign
        const nowDate = dayjs()
        const isNowThisWeekday = hasOperatingRegions && nowDate.day() === weekday

        const result: SelectedDate[] = []
        if (isNowThisWeekday) {
          result.push({
            date: NOW_DATE,
            label: `${nowDate.locale(locale).format('dddd D MMMM')} ${nowSuffix}`,
            color: CHART_COLORS[0],
            eveningIds: getEveningIdsForDate(NOW_DATE),
          })
        }
        matchingDates.forEach((d) => {
          result.push({
            date: d.date,
            label: dayjs(d.date).locale(locale).format('dddd D MMMM'),
            color: CHART_COLORS[result.length % CHART_COLORS.length],
            eveningIds: getEveningIdsForDate(d.date),
          })
        })
        return result
      }

      case 'isoWeek': {
        // Find dates with the same ISO week and weekday across all years
        if (!referenceDate) return []

        // Use dayjs for parsing to avoid timezone issues with ISO date strings
        const refDate = referenceDate === NOW_DATE ? new Date() : dayjs(referenceDate).toDate()
        const targetIsoWeek = getIsoWeek(refDate)
        const targetIsoWeekday = getIsoWeekday(refDate)

        // Find all dates that match the target ISO week and weekday
        const matchingDates: Array<{ date: string; year: number; isNow: boolean }> = []

        // Check "now" first if it matches
        if (hasOperatingRegions) {
          const nowDate = new Date()
          if (getIsoWeek(nowDate) === targetIsoWeek && getIsoWeekday(nowDate) === targetIsoWeekday) {
            matchingDates.push({
              date: NOW_DATE,
              year: nowDate.getFullYear(),
              isNow: true,
            })
          }
        }

        // Check all closed dates
        availableClosedDates.forEach((d) => {
          const dateObj = dayjs(d.date).toDate()
          if (getIsoWeek(dateObj) === targetIsoWeek && getIsoWeekday(dateObj) === targetIsoWeekday) {
            // Don't duplicate if "now" already added for this date
            const todayStr = dayjs().format('YYYY-MM-DD')
            if (!(hasOperatingRegions && d.date === todayStr)) {
              matchingDates.push({
                date: d.date,
                year: dateObj.getFullYear(),
                isNow: false,
              })
            }
          }
        })

        // Sort by year descending (most recent first)
        matchingDates.sort((a, b) => b.year - a.year)

        return matchingDates.map((m, index) => {
          const dateObj = m.isNow ? new Date() : dayjs(m.date).toDate()
          const baseLabel = `${m.year} - ${dayjs(dateObj).locale(locale).format('dddd D MMMM')}`
          const label = m.isNow ? `${baseLabel} ${nowSuffix}` : baseLabel

          return {
            date: m.date,
            label,
            color: CHART_COLORS[index % CHART_COLORS.length],
            eveningIds: getEveningIdsForDate(m.date),
          }
        })
      }

      default:
        return []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    dates,
    fixedDay,
    weekday,
    referenceDate,
    regionId,
    locale,
    availableDates,
    availableClosedDates,
    nowEveningIdMap,
    closedEveningsByDateAndRegion,
    hasOperatingRegions,
    currentCampaignYear,
    nowSuffix,
  ])

  // Get all unique evening IDs to query and track which are "live" (from NOW_DATE)
  // IMPORTANT: Stabilize these values using string keys to prevent query key thrashing
  // that can cause Firestore listener accumulation (memory leak)
  const allEveningIdsKey = useMemo(
    () =>
      uniq(selectedDatesWithEveningIds.flatMap((d) => d.eveningIds))
        .sort((a, b) => a - b)
        .join(','),
    [selectedDatesWithEveningIds]
  )

  const allEveningIds = useMemo(
    () => (allEveningIdsKey ? allEveningIdsKey.split(',').map(Number) : []),
    [allEveningIdsKey]
  )

  const liveEveningIdsKey = useMemo(
    () =>
      selectedDatesWithEveningIds
        .filter((d) => d.date === NOW_DATE)
        .flatMap((d) => d.eveningIds)
        .sort((a, b) => a - b)
        .join(','),
    [selectedDatesWithEveningIds]
  )

  const liveEveningIds = useMemo(
    () => new Set(liveEveningIdsKey ? liveEveningIdsKey.split(',').map(Number) : []),
    [liveEveningIdsKey]
  )

  // Create queries for all needed evening transport requests
  // Use statsTransportRequestsQuery which includes 'submitted' status (newly received requests)
  // For closed evenings (not in liveEveningIds), use one-time reads instead of snapshots
  const transportRequestQueries = useMemo(
    () => allEveningIds.map((eveningId) => statsTransportRequestsQuery(eveningId, liveEveningIds.has(eveningId))),
    [allEveningIds, liveEveningIds]
  )
  const transportRequestResults = useQueries({ queries: transportRequestQueries })

  // isLoading: true only on initial load (no cached data)
  // isFetching: true when fetching, even if we have stale/cached data to show
  const isLoading = transportRequestResults.some((r) => r.isLoading)
  const isFetching = transportRequestResults.some((r) => r.isFetching)

  // Build a map from eveningId to its transport requests
  // Use dataUpdatedAt to detect when Firestore pushes new data (this timestamp changes on every update)
  // Stabilize the dependency by joining timestamps into a string (arrays are recreated on every render)
  const dataUpdatedKey = transportRequestResults.map((r) => r.dataUpdatedAt).join(',')
  const transportRequestsByEveningId = useMemo(() => {
    const map: Record<number, Array<{ submitTimestamp: Date }>> = {}
    allEveningIds.forEach((eveningId, index) => {
      const data = transportRequestResults[index]?.data
      if (data) {
        const requests: Array<{ submitTimestamp: Date }> = []
        for (const tr of data.data) {
          if (tr.submitTimestamp != null) {
            requests.push({ submitTimestamp: tr.submitTimestamp })
          }
        }
        map[eveningId] = requests
      } else {
        map[eveningId] = []
      }
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataUpdatedKey triggers recalculation when Firestore data updates
  }, [allEveningIds, dataUpdatedKey])

  // Transform transport requests into chart data (using shared utility)
  const freshChartData = useMemo(
    (): ChartDataPoint[] =>
      buildChartData({
        selectedDates: selectedDatesWithEveningIds,
        transportRequestsByEveningId,
        now,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataUpdatedKey ensures recalculation when Firestore data updates
    [selectedDatesWithEveningIds, transportRequestsByEveningId, now, dataUpdatedKey]
  )

  // Keep previous chart data stable while fetching new data
  // This prevents showing incomplete/empty charts during loading
  const stableChartDataRef = useRef<ChartDataPoint[]>(freshChartData)
  const stableSelectedDatesRef = useRef<SelectedDate[]>(selectedDatesWithEveningIds)

  // Only update stable data when not fetching (i.e., when fresh data is ready)
  if (!isFetching) {
    stableChartDataRef.current = freshChartData
    stableSelectedDatesRef.current = selectedDatesWithEveningIds
  }

  return {
    availableRegions,
    availableDates,
    currentCampaignDates,
    selectedDates: stableSelectedDatesRef.current,
    chartData: stableChartDataRef.current,
    isLoading,
    isFetching,
  }
}
